import { createContext, useContext } from 'react'
import type {
  CreateGenerationRequest,
  CreateGenerationResponse,
  GenerationTaskSummary,
} from '@/api/generation'

const TERMINAL = new Set(['success', 'failed', 'blocked', 'timeout'])

export function availableGenerationSlots(activeCount: number, activeLimit: number): number {
  return Math.max(0, activeLimit - activeCount)
}

export function shouldShowGenerationCapacity(
  capacityReady: boolean,
  activeCount: number,
  activeLimit: number,
): boolean {
  return capacityReady && activeCount > 0 && activeLimit > 0
}

export function taskSummaryFromCreateAck(
  created: CreateGenerationResponse,
  payload: CreateGenerationRequest,
  createdAt = new Date().toISOString(),
): GenerationTaskSummary {
  return {
    gen_id: created.gen_id,
    client_request_id: created.client_request_id ?? payload.idempotency_key,
    status: created.status,
    status_version: created.status_version ?? 1,
    status_updated_at: createdAt,
    progress_stage: created.progress_stage ?? created.status,
    points_state: created.points_state ?? 'reserved',
    prompt: payload.prompt,
    created_at: createdAt,
    retry_action: 'none',
    fallback_used: false,
    can_retry: false,
    model_id: payload.model_id ?? '',
    quality: payload.quality ?? 'standard',
    ratio: payload.ratio,
    style_id: payload.style_id || undefined,
    mode: payload.mode ?? 'text2img',
    tool_id: payload.tool_id,
    entry_id: payload.entry_id,
    points_cost: created.points_cost ?? created.cost_points ?? 0,
    parent_gen_id: payload.parent_gen_id,
    favorited: false,
  }
}

export function mergeGenerationTasks(
  current: Record<string, GenerationTaskSummary>,
  incoming: GenerationTaskSummary[],
): {
  next: Record<string, GenerationTaskSummary>
  completed: GenerationTaskSummary[]
  conflicts: GenerationTaskSummary[]
} {
  const next = { ...current }
  const completed: GenerationTaskSummary[] = []
  const conflicts: GenerationTaskSummary[] = []
  const batch: Record<string, GenerationTaskSummary> = {}
  for (const task of incoming) {
    const candidate = batch[task.gen_id]
    if (!candidate || task.status_version > candidate.status_version) {
      batch[task.gen_id] = task
      continue
    }
    if (task.status_version === candidate.status_version && task.status !== candidate.status) {
      conflicts.push(task)
      if (TERMINAL.has(task.status) && !TERMINAL.has(candidate.status)) batch[task.gen_id] = task
    }
  }
  for (const task of Object.values(batch)) {
    const previous = next[task.gen_id]
    if (previous && task.status_version === previous.status_version && task.status !== previous.status) {
      conflicts.push(task)
      continue
    }
    if (previous && task.status_version === previous.status_version) {
      next[task.gen_id] = { ...previous, favorited: task.favorited }
      continue
    }
    if (previous && task.status_version <= previous.status_version) continue
    if (previous && TERMINAL.has(previous.status) && task.status !== previous.status) {
      conflicts.push(task)
      continue
    }
    next[task.gen_id] = task
    if (previous && !TERMINAL.has(previous.status) && TERMINAL.has(task.status)) {
      completed.push(task)
    }
  }
  return { next, completed, conflicts }
}

export type GenerationTaskContextValue = {
  tasks: GenerationTaskSummary[]
  activeCount: number
  activeLimit: number
  capacityReady: boolean
  refresh: () => Promise<void>
  acceptCreateAck: (
    created: CreateGenerationResponse,
    payload: CreateGenerationRequest,
  ) => void
  applyCapacity: (activeCount: number, activeLimit: number) => void
}

export const GenerationTaskContext = createContext<GenerationTaskContextValue>({
  tasks: [],
  activeCount: 0,
  activeLimit: 0,
  capacityReady: false,
  refresh: async () => undefined,
  acceptCreateAck: () => undefined,
  applyCapacity: () => undefined,
})

export const useGenerationTasks = () => useContext(GenerationTaskContext)
